import { useState, useEffect } from 'react';
import { apiPost } from '../../api/axios';
import { getToken } from '../../auth/token';

export default function ProfilePage() {
  const token = getToken();
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
  });
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
  });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await apiPost({ action: 'get_profile' });
      if (res.flag === 'success') {
        const user = res.data;
        setProfile(user);
        setForm({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          phone: user.phone || '',
          currentPassword: '',
          newPassword: '',
        });
      }
    } catch {
      setMsg({ type: 'error', text: 'Failed to load profile.' });
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });
    setLoading(true);

    const payload = {
      action: 'update_profile',
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
    };

    if (form.currentPassword && form.newPassword) {
      payload.password = form.currentPassword;
      payload.new_password = form.newPassword;
    }

    try {
      const res = await apiPost(payload);
      if (res.flag === 'success') {
        setMsg({ type: 'success', text: 'Profile updated successfully.' });
        setIsEditing(false);
        fetchProfile();
      } else {
        setMsg({ type: 'error', text: res.msg || 'Update failed.' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.msg || err.message });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setMsg({ type: '', text: '' });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: profile.email || '',
      phone: profile.phone || '',
      currentPassword: '',
      newPassword: '',
    });
  };

  const closeAlert = () => setMsg({ type: '', text: '' });

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Profile</h2>

      {/* Alert message */}
      {msg.text && (
        <div
          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
            msg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400'
              : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
          }`}
        >
          <svg className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            {msg.type === 'success' ? (
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            ) : (
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            )}
          </svg>
          <div className="flex-1">{msg.text}</div>
          <button onClick={closeAlert} className="text-current opacity-50 hover:opacity-100">&times;</button>
        </div>
      )}

      {/* View mode card */}
      {!isEditing && (
        <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-3xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <InfoItem label="Username" value={profile.username} />
            <InfoItem label="Role" value={profile.role} />
            <InfoItem label="First Name" value={profile.first_name || '—'} />
            <InfoItem label="Last Name" value={profile.last_name || '—'} />
            <InfoItem label="Email" value={profile.email} />
            <InfoItem label="Phone" value={profile.phone || '—'} />
          </div>
          <div className="mt-8">
            <button
              onClick={startEditing}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white font-medium shadow-lg shadow-brand-500/25 transition-all duration-200 hover:scale-[1.02]"
            >
              Update Information
            </button>
          </div>
        </div>
      )}

      {/* Edit mode card */}
      {isEditing && (
        <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-3xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-8">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Edit Profile</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InputField label="Username" value={profile.username} disabled />
              <InputField label="Role" value={profile.role} disabled />
              <InputField label="First Name" name="first_name" value={form.first_name} onChange={handleChange} />
              <InputField label="Last Name" name="last_name" value={form.last_name} onChange={handleChange} />
              <InputField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
              <InputField label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            </div>

            <hr className="border-border-light dark:border-border-dark" />

            <h4 className="text-lg font-medium text-slate-900 dark:text-white">Change Password (optional)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InputField
                label="Current Password"
                name="currentPassword"
                type="password"
                value={form.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
              />
              <InputField
                label="New Password"
                name="newPassword"
                type="password"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white font-medium shadow-lg shadow-brand-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {loading ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                className="px-6 py-2.5 rounded-2xl border-2 border-border-light dark:border-border-dark text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-medium transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Reusable info display component (view mode)
function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-slate-900 dark:text-white font-medium mt-1">{value}</p>
    </div>
  );
}

// Reusable input field component (edit mode)
function InputField({ label, name, type = 'text', value, onChange, required, disabled, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-2xl border-2 border-border-light dark:border-border-dark bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-brand-500 dark:focus:border-brand-400 focus:ring-4 focus:ring-brand-500/20 dark:focus:ring-brand-400/20 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
      />
    </div>
  );
}